import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Dropdown } from "antd";
import { Button } from "@/shared/components/button";
import { PillTabs } from "@/shared/components/PillTabs";
import { Spinner } from "@/shared/components/spinner";
import { EmptyState } from "@/shared/components/EmptyState";
import { cn } from "@/shared/lib/utils";
import { toastError } from "@/shared/lib/toast";
import { getPatientRecordsDetailed, getUploadRecordsOfType, type ParsedRecord, type SourceGroup } from "@/features/patient/records/api/dataPipelineApi";
import {
  collapsePractitionerDuplicates,
  documentLabel,
  extractedDocumentRecords,
  formatLabel,
  formatValue,
  groupClinicalRecords,
  visibleFields,
  withMoreRecords,
  withTypeTotals,
} from "@/features/patient/records/utils/parsedDataPresentation";
import { formatFileSize, groupIssues } from "@/features/patient/records/utils/uploadIssuePresentation";
import { RecordsFilterModal } from "@/features/patient/records/components/RecordsFilterModal";
import { ExtractedTextPanel } from "@/features/patient/records/components/ExtractedTextPanel";
import { OriginalFileTab } from "@/features/patient/records/components/OriginalFileTab";
import { UploadIssueSections } from "@/features/patient/records/components/UploadIssueSections";
import type { ZipUploadEntry } from "@/features/patient/records/components/UploadHistoryList";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { RECORDS_POLL_MS, POLL_BACKOFF_MAX_MS } from "@/features/patient/records/utils/pollingConfig";
import { startBackoffPolling } from "@/shared/lib/pollingBackoff";
import { LIVE_BACKUP_POLL_MS } from "@/features/patient/records/utils/liveUploadEvents";
import { UploadCareSummary } from "@/features/patient/care-treatment/components/WhatWeFound";

type TabId = "issues" | "files" | SourceGroup | "original";

// Always shown, regardless of what this particular upload actually contains — an
// upload with no PDF/scanned content just renders an empty state under "View PDF
// Data" (matching the Issues tab's own "nothing here" treatment) rather than the
// tab disappearing, so the layout doesn't shift between uploads.
const DATA_TABS: { id: SourceGroup; label: string }[] = [
  { id: "structured", label: "Parsed Data" },
  { id: "pdf", label: "View PDF Data" },
];

// Self-explanatory fourth splitter, alongside Issues/Parsed Data/View PDF
// Data — the real file (PDF or image) next to what we read out of it. See
// OriginalFileTab. Not a SourceGroup: it doesn't fetch FHIR records, it
// fetches the document's own original file + its per-page extraction.
const ORIGINAL_FILE_TAB_ID = "original" as const;

// Sentinel viewingDocumentId used when the patient presses "View Extracted
// Text" before this upload has produced any extractable document yet (the
// document_extraction_status row isn't there, so extractedDocumentRecords is
// still empty — see its docstring). Rather than the click doing nothing
// ("looks like it's not even pressed"), the body swaps to a plain
// "not ready yet" state, same idea as ExtractedTextPanel's own processing
// banner but without needing a real document id to poll against.
const EXTRACTION_PENDING_ID = "__extraction_pending__" as const;

async function fetchWithRetry<T>(fn: () => Promise<T>, isCancelled: () => boolean, attempts = 3): Promise<T> {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === attempts || isCancelled()) throw err;
      await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
    }
  }
  throw new Error("unreachable");
}

const FILES_TAB_ID = "files" as const;

const FILE_ICONS: Record<string, string> = {
  pdf: "lucide:file-text",
  xml: "lucide:file-code",
  json: "lucide:file-json",
  ndjson: "lucide:file-json",
  tsv: "lucide:file-spreadsheet",
  csv: "lucide:file-spreadsheet",
  jpg: "lucide:file-image",
  jpeg: "lucide:file-image",
  png: "lucide:file-image",
  tif: "lucide:file-image",
  tiff: "lucide:file-image",
  bmp: "lucide:file-image",
};

function splitPath(path: string): { folder: string; name: string; ext: string } {
  const slash = path.lastIndexOf("/");
  const name = slash === -1 ? path : path.slice(slash + 1);
  const dot = name.lastIndexOf(".");
  return { folder: slash === -1 ? "" : path.slice(0, slash), name, ext: dot > 0 ? name.slice(dot + 1).toLowerCase() : "" };
}

/** The files inside a multi-file upload (a zip), one row each, grouped by folder. */
function FilesFoundList({ files }: { files: { name: string; size: number }[] }) {
  const folders = new Map<string, { name: string; ext: string; size: number }[]>();
  for (const file of files) {
    const { folder, name, ext } = splitPath(file.name);
    folders.set(folder, [...(folders.get(folder) ?? []), { name, ext, size: file.size }]);
  }
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  return (
    <div className="space-y-4">
      <p className="text-sm text-[#6B7280]">
        {files.length} files, {formatFileSize(totalSize)} in total
      </p>
      {[...folders.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([folder, items]) => (
        <div key={folder || "/"}>
          {folder && (
            <div className="mb-2 flex items-center gap-2">
              <Icon icon="lucide:folder" className="h-4 w-4 text-[#9CA3AF]" />
              <h3 className="truncate text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">
                {folder} ({items.length})
              </h3>
            </div>
          )}
          <ul className="divide-y divide-[#F0F1F3] overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
            {[...items].sort((a, b) => a.name.localeCompare(b.name)).map((item, index) => (
              <li key={`${item.name}-${index}`} className="flex items-center gap-3 px-4 py-2.5">
                <Icon icon={FILE_ICONS[item.ext] ?? "lucide:file"} className="h-4 w-4 shrink-0 text-[#6B7280]" />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#111827]" title={item.name}>
                  {item.name}
                </span>
                {item.ext && (
                  <span className="shrink-0 rounded-md bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">
                    {item.ext}
                  </span>
                )}
                <span className="w-16 shrink-0 text-right text-xs tabular-nums text-[#9CA3AF]">{formatFileSize(item.size)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function UploadDetailPanel({
  entry,
  actingAsProfileId,
  isLoadingContents = false,
}: {
  entry: ZipUploadEntry;
  actingAsProfileId?: string;
  /** True while this upload's files/parsingIssues are still being fetched
   *  (see UploadHistoryView) — only the Issues tab's own empty state needs
   *  to know, so it can show "still checking" instead of falsely claiming
   *  "no issues" before that fetch has actually resolved. Everything else
   *  in this panel (header, tabs, the other two data tabs) doesn't depend
   *  on this fetch and renders immediately regardless. */
  isLoadingContents?: boolean;
}) {
  const [records, setRecords] = useState<ParsedRecord[] | null>(null);
  // Each record type's total in the whole upload (records is only the newest page).
  const [typeCounts, setTypeCounts] = useState<Record<string, number> | null>(null);
  // "Show more" pages per record type, on top of the preview's newest few.
  const [moreByType, setMoreByType] = useState<Record<string, ParsedRecord[]>>({});
  const [loadingMoreType, setLoadingMoreType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const retryLoad = () => setRetryToken((t) => t + 1);

  const [activeTab, setActiveTab] = useState<TabId>("issues");
  // Which upload + tab is on screen now: a "Show more" answer for another one is dropped.
  const viewKeyRef = useRef("");
  viewKeyRef.current = `${entry.uploadId}|${activeTab}`;
  const [filterTypes, setFilterTypes] = useState<string[] | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  // Which document "View Original File" is showing, when this upload found
  // more than one. Null means "not chosen yet" — defaults to the first
  // available document (see effectiveDocumentRecords below).
  const [selectedOriginalDocId, setSelectedOriginalDocId] = useState<string | null>(null);
  // The original "View Extracted Text" button/dropdown — kept alongside the
  // newer View Original File tab (not replaced by it): set, this overlays
  // ExtractedTextPanel over whatever tab is otherwise active, exactly as it
  // always has.
  const [viewingDocumentId, setViewingDocumentId] = useState<string | null>(null);

  const { getCachedRecords, cacheRecords, onUploadChanged } = useUploadHistory();

  // Real server id only — stays undefined during the brief "uploading"/
  // "queued" window right after Upload is pressed, before the POST response
  // lands and assigns one (see UploadRecords.tsx's startDocumentUpload). The
  // records effect below must not fall back to entry.id (a local
  // crypto.randomUUID(), meaningless to the backend) the way the tab-reset
  // effect further down deliberately does — a fetch keyed on that fake id
  // would ask the API for an upload it's never heard of, get back a
  // harmless-looking empty result, cache it, then get torn down and
  // restarted the instant the real id arrives moments later (uploadId is a
  // dependency of that effect), flashing "Loading…" a second time for no
  // reason. Gating the whole effect on the real id existing avoids that
  // wasted fetch and the spurious reload it causes.
  const uploadId = entry.uploadId;

  // Switching to a new upload lands on its preferred tab, with a clean slate —
  // matches the "select an upload" default from the sidebar. Ordinarily
  // that's Issues; entry.preferredTab (set once at creation — see
  // UploadRecords.tsx's handleUpload) overrides it to "pdf" for a single
  // plain PDF upload, since Issues is almost always empty for one of those.
  // Keyed on entry.id, NOT uploadId: a freshly-submitted upload starts out
  // with no uploadId at all (only the local entry.id), and gets one assigned
  // moments later once the POST reaches the backend — the same update that
  // also flips the toast stage to "Received". uploadId (entry.uploadId ??
  // entry.id) would flip value right then even though it's still the same
  // upload, spuriously re-running this effect and stomping the patient's tab
  // selection back to the default the instant that toast update landed.
  // entry.id is assigned once and never reassigned, so it only changes when
  // the patient genuinely selects a different upload.
  useEffect(() => {
    // A direct-API sync entry (Epic, etc.) always opens on Parsed Data,
    // regardless of preferredTab — it has no zip/file Issues to show, ever.
    // Checked directly here (not only relying on mapSyncJobToEntry/
    // addZipUpload setting preferredTab) as a backstop: a sync entry can be
    // rebuilt by more than one code path, and this is the one place that
    // actually decides what the patient lands on.
    setActiveTab(entry.kind === "sync" ? "structured" : (entry.preferredTab ?? "issues"));
    setFilterTypes(null);
    setSelectedOriginalDocId(null);
    setViewingDocumentId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, entry.kind]);

  // Issues never need a records fetch (that data comes from entry.parsingIssues).
  // Parsed Data / View PDF Data each fetch their own source-group-scoped slice,
  // re-fetched on every tab switch since the two are independent record sets —
  // but only ever from the network the FIRST time a given (upload, tab) pair
  // is viewed this session. UploadHistoryContext's in-memory cache (never
  // localStorage — see its own docstring, this is PHI) means switching back
  // to an already-seen upload or tab redisplays instantly instead of blanking
  // to a spinner and re-fetching from scratch.
  //
  // While this upload is still "processing", the set behind it keeps growing —
  // an on-interval re-fetch is layered on top of the same effect (entry.status
  // is a dependency, so a status transition tears the old interval down and
  // runs this fresh) instead of a one-shot fetch, so the counts below visibly
  // grow page by page rather than only appearing once the whole job finishes.
  //
  // Only a cache entry marked isFinal skips the network call entirely on
  // revisit. A non-final (or missing) entry always does at least one fresh
  // fetch even if entry.status is already terminal — because the cache and
  // the upload's status come from two independently-polled sources, a
  // snapshot cached while still "processing" can predate data that only
  // appears once conversion finishes, and the status can flip to terminal
  // moments later without this component ever seeing that gap. See
  // UploadHistoryContext's getCachedRecords docstring for the full story.
  useEffect(() => {
    // Neither Issues nor View Original File fetch FHIR records here — Issues
    // reads entry.parsingIssues directly, and View Original File fetches its
    // own document file + per-page text inside OriginalFileTab, keyed off
    // effectiveDocumentRecords below (itself sourced from the "pdf" source
    // group, same as the View PDF Data tab).
    if (activeTab === "issues" || activeTab === FILES_TAB_ID || activeTab === ORIGINAL_FILE_TAB_ID) { setRecords(null); setTypeCounts(null); setError(null); return; }
    if (!uploadId) {
      // Still uploading/queued — no real upload id to fetch or cache
      // against yet. Render as "nothing found so far" (same branch the
      // processing placeholder below already uses) instead of "Loading…";
      // the moment the real id lands, this effect re-runs and does the one
      // genuine fetch.
      setRecords([]);
      setTypeCounts(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setError(null);

    const cached = getCachedRecords(uploadId, activeTab);
    setRecords(cached ? cached.detail.records : null);
    setTypeCounts(cached ? cached.detail.typeCounts ?? null : null);
    setMoreByType({});

    // startBackoffPolling below only starts its next fetch after the current
    // one settles, so consecutive poll ticks can't overlap each other — but
    // the very first genuine fetch (load(false), just below) and the
    // poller's first tick still both exist independently, and over a slow
    // or variable connection their responses can arrive out of order.
    // Without requestId, whichever response happened to resolve last would
    // win even if it were carrying OLDER data, silently reverting the view —
    // and if that stale response landed right as the upload turned terminal,
    // it would get cached as isFinal and stick that way permanently. Only
    // ever apply the response belonging to the most recently issued
    // request; a superseded one is just discarded.
    let latestRequestId = 0;
    // background=true for the live-poll's own recurring re-fetch (a UI
    // polling tick, not the patient actually looking) — skips the backend's
    // VIEW audit log for that call; background=false for the one fetch per
    // effect run that represents a genuine look (first load, tab switch,
    // manual retry), which still logs normally.
    const load = (background: boolean) => {
      const requestId = ++latestRequestId;
      return getPatientRecordsDetailed({ uploadId, sourceGroup: activeTab, actingAsProfileId, background }).then((result) => {
        if (cancelled || requestId !== latestRequestId) return;
        setRecords(result.records);
        setTypeCounts(result.typeCounts ?? null);
        cacheRecords(uploadId, activeTab, result, entry.status !== "processing");
      });
    };

    let poller: ReturnType<typeof startBackoffPolling> | undefined;
    let onVisibilityChange: (() => void) | undefined;
    let stopListening: (() => void) | undefined;
    if (cached?.isFinal) {
      // Confirmed final — this exact snapshot was itself fetched after the
      // upload was already terminal, so nothing left to fetch or watch for.
    } else {
      fetchWithRetry(() => load(false), () => cancelled).catch(() => {
        // A cached view already has something real to show — a failed
        // (re)fetch on top of that stays silent rather than replacing it
        // with an error banner; only a true first-load failure surfaces one.
        if (!cancelled && !cached) setError("Couldn't load the data found in this file. Please try again.");
      });

      if (entry.status === "processing") {
        // Backs off on consecutive failures (see pollingBackoff.ts) instead
        // of retrying a struggling connection at full rate, and genuinely
        // suspends — not just "also fetches on return" — while this tab is
        // hidden: a backgrounded tab produces zero network activity here
        // until it's visible again, at which point resume() does one
        // immediate catch-up poll rather than waiting out a stale timer.
        poller = startBackoffPolling({
          intervalMs: RECORDS_POLL_MS,
          maxIntervalMs: POLL_BACKOFF_MAX_MS,
          poll: () => load(true),
        });
        if (document.visibilityState === "hidden") poller.pause();

        onVisibilityChange = () => {
          if (document.visibilityState === "visible") poller?.resume();
          else poller?.pause();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("focus", onVisibilityChange);

        // Live updates (connection plan step 5): the server says this upload
        // changed, so its records are fetched now (never more often than the
        // normal pace), and the timer only backs those messages up. If live
        // messages stop, the timer goes back to the normal pace.
        stopListening = onUploadChanged?.(uploadId, (signal) => {
          if (signal === "offline") {
            poller?.setIntervalMs(RECORDS_POLL_MS);
            return;
          }
          poller?.setIntervalMs(LIVE_BACKUP_POLL_MS);
          poller?.pollNow(RECORDS_POLL_MS);
        });
      }
    }

    return () => {
      cancelled = true;
      poller?.stop();
      stopListening?.();
      if (onVisibilityChange) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("focus", onVisibilityChange);
      }
    };
  }, [uploadId, actingAsProfileId, activeTab, retryToken, entry.status, getCachedRecords, cacheRecords, onUploadChanged]);

  // Independent of which tab is active. A zip upload — including one whose
  // only content turned out to be a plain PDF (see upload_service.py's
  // vendor="document" fallback) — always defaults to the Issues tab (see
  // UploadRecords.tsx's directUploadCategory, which deliberately never
  // guesses a zip's contents), so without this a patient would never see
  // "View Extracted Text" until they happened to click into "View PDF Data"
  // themselves, even though the extracted content was ready the whole time.
  // Skips entirely once the patient IS on that tab — the main tab-fetch
  // effect above already owns "pdf" then (including its own live poll while
  // processing), so this would just be a second, redundant poller running
  // against the same source group.
  //
  // Polls on the same cadence as the main tab-fetch effect while the upload
  // is still "processing" (a large PDF — many pages of OCR + LLM conversion
  // — can take a while, and the extract button should turn from a skeleton
  // into the real thing the moment content actually lands, not only once
  // the whole job finishes) — but only ever ONE of the two pollers is ever
  // running for a given upload at a time: this one for as long as the
  // patient stays off "View PDF Data", the main effect's own the moment
  // they switch to it (which tears this one down, see the early return
  // above).
  const [pdfDocumentRecords, setPdfDocumentRecords] = useState<ParsedRecord[]>([]);
  useEffect(() => {
    if (!uploadId || activeTab === "pdf") { return; }
    let cancelled = false;

    const cached = getCachedRecords(uploadId, "pdf");
    if (cached) setPdfDocumentRecords(extractedDocumentRecords(cached.detail.records));
    if (cached?.isFinal) return;

    const load = () =>
      getPatientRecordsDetailed({ uploadId, sourceGroup: "pdf", actingAsProfileId, background: true }).then((result) => {
        if (cancelled) return;
        setPdfDocumentRecords(extractedDocumentRecords(result.records));
        // Never overwrite a fresher isFinal snapshot the main tab-fetch
        // effect above may already hold for this exact (uploadId, "pdf")
        // pair — this background check only fills the cache when nothing
        // final is there yet.
        if (!getCachedRecords(uploadId, "pdf")?.isFinal) {
          cacheRecords(uploadId, "pdf", result, entry.status !== "processing");
        }
      });

    // Retried (not fired once and forgotten): a single failed request here
    // used to leave pdfDocumentRecords permanently empty and the cache never
    // marked final — since polling below only ever starts for a still-
    // "processing" upload, an already-complete upload had no second chance
    // at all, which showed as the "View Extracted Text" button being stuck
    // on its loading skeleton forever with nothing to click. The main
    // tab-fetch effect is still the source of truth for "View PDF Data"
    // itself; this one only has to stop permanently wedging the button.
    fetchWithRetry(load, () => cancelled).catch(() => {
      // Still best-effort after retries are exhausted — this only controls
      // whether the extract button appears a little earlier than the
      // patient manually visiting "View PDF Data".
    });

    let poller: ReturnType<typeof startBackoffPolling> | undefined;
    if (entry.status === "processing") {
      poller = startBackoffPolling({ intervalMs: RECORDS_POLL_MS, maxIntervalMs: POLL_BACKOFF_MAX_MS, poll: load });
      if (document.visibilityState === "hidden") poller.pause();
    }
    // Paused while the tab is hidden, like the main poll above.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") poller?.resume();
      else poller?.pause();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      poller?.stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [uploadId, activeTab, actingAsProfileId, entry.status, getCachedRecords, cacheRecords]);

  const files = entry.files ?? [];
  // Only an upload holding several files (a zip) gets the Files Found tab;
  // a single PDF or C-CDA has nothing to list.
  const showFilesTab = files.length > 1;
  const issues = entry.parsingIssues ?? [];
  // Only files that couldn't be read and parts that couldn't be imported count
  // as "not imported" — a note is a record that WAS imported.
  const groupedIssues = groupIssues(issues);
  const notImported = groupedIssues.unreadable.length + groupedIssues.not_imported.length;
  const hasIssues = notImported > 0;

  const dataTabs = DATA_TABS;

  const selectDataTab = (id: TabId) => {
    setActiveTab(id);
    setFilterTypes(null);
    setViewingDocumentId(null);
  };

  const shownRecords = withMoreRecords(records ?? [], moreByType);
  const grouped = withTypeTotals(groupClinicalRecords(shownRecords), typeCounts);
  const loadMore = (type: string, loaded: number) => {
    if (!uploadId || (activeTab !== "structured" && activeTab !== "pdf")) return;
    const sourceGroup = activeTab;
    const viewKey = viewKeyRef.current;
    setLoadingMoreType(type);
    getUploadRecordsOfType({ uploadId, resourceType: type, offset: loaded, sourceGroup, actingAsProfileId })
      .then(({ records: more }) => {
        if (viewKeyRef.current !== viewKey) return; // another upload or tab is on screen now
        setMoreByType((current) => ({ ...current, [type]: [...(current[type] ?? []), ...more] }));
      })
      .catch(() => toastError("Couldn't load more records. Please try again."))
      .finally(() => setLoadingMoreType((current) => (current === type ? null : current)));
  };
  const visibleGrouped = filterTypes === null ? grouped : grouped.filter((g) => filterTypes.includes(g.type));
  const documentRecords = extractedDocumentRecords(records ?? []);
  // Filtering (RecordsFilterModal) only applies to the two record-list tabs —
  // Issues reads a fixed list, and View Original File shows a document
  // picker instead of a filterable record list. Always shown on those two
  // tabs regardless of whether any data has arrived yet — the tab body's own
  // empty state (below) is what tells the patient there's nothing there, not
  // the filter control appearing/disappearing out from under them.
  const showFilterButton = activeTab === "structured" || activeTab === "pdf";

  // Whichever source actually reflects "pdf" content right now: the
  // currently-active tab's own fetch when already on "View PDF Data"
  // (freshest — records for that exact tab), otherwise the independent
  // background check above — so View Original File's document picker
  // doesn't wait on the patient manually visiting "View PDF Data" first
  // (see pdfDocumentRecords' own effect for why that's needed at all).
  const effectiveDocumentRecords = activeTab === "pdf" ? documentRecords : pdfDocumentRecords;

  // Whether "this upload has zero extracted PDF/image content" is actually a
  // CONFIRMED fact yet, vs. simply not-arrived-yet — a large PDF can take a
  // while to extract (many pages), and until either the "pdf" tab's own
  // fetch or the background check above has landed a snapshot fetched while
  // the upload was already terminal (isFinal — see getCachedRecords'
  // docstring), an empty effectiveDocumentRecords just means "don't know
  // yet," not "there is nothing." Without this distinction View Original
  // File would flash "no file" for the entire time a big upload was still
  // extracting, before ever getting a chance to find one.
  const pdfCache = uploadId ? getCachedRecords(uploadId, "pdf") : undefined;
  const isPdfContentConfirmedAbsent = effectiveDocumentRecords.length === 0 && !!pdfCache?.isFinal;

  const selectedOriginalDoc =
    effectiveDocumentRecords.find((d) => d.id === selectedOriginalDocId) ?? effectiveDocumentRecords[0] ?? null;

  // The "View Extracted Text" button — gated exactly like the filter button
  // (showFilterButton above), nothing more: purely a function of which tab is
  // active, with the one extra restriction that it's the "View PDF Data" tab
  // only, never "Parsed Data". Like the filter button it does NOT wait on
  // data — it's simply there the whole time that tab is open (no skeleton, no
  // loading swap, no layout shift). It only ever becomes a dropdown instead
  // of a plain button when this upload genuinely has more than one extracted
  // document to choose between; with zero or one it's the plain button.
  const viewDocument = (id: string) => {
    setViewingDocumentId(id);
  };

  // If the patient opened the "not ready yet" state (sentinel) and the
  // background poll has since landed an actual extracted document, swap them
  // straight into it — no need to close and press the button again.
  useEffect(() => {
    if (viewingDocumentId === EXTRACTION_PENDING_ID && effectiveDocumentRecords[0]) {
      setViewingDocumentId(effectiveDocumentRecords[0].id);
    }
  }, [viewingDocumentId, effectiveDocumentRecords]);

  const showExtractButton = activeTab === "pdf";
  const extractIsDropdown = effectiveDocumentRecords.length > 1;
  // Always transitions the body to *something* — an extracted document if one
  // exists, otherwise the EXTRACTION_PENDING_ID sentinel that renders the
  // "not ready yet" state below. It must never be a silent no-op: before this
  // upload has produced a document_extraction_status row the click would
  // otherwise do nothing and read as an unresponsive button.
  const openFirstExtractedDoc = () => {
    setViewingDocumentId(effectiveDocumentRecords[0]?.id ?? EXTRACTION_PENDING_ID);
  };

  let summary: { text: string; tone: "issue" | "neutral" } | null = null;
  if (files.length > 0 && hasIssues) {
    summary = { text: `${files.length} file${files.length === 1 ? "" : "s"} found; ${notImported} couldn't be imported.`, tone: "issue" };
  } else if (files.length > 0) {
    summary = { text: `${files.length} file${files.length === 1 ? "" : "s"} found.`, tone: "neutral" };
  } else if (hasIssues) {
    summary = { text: `${notImported} couldn't be imported.`, tone: "issue" };
  }

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0">
        <h2 className="text-lg font-extrabold text-[#0A2947]">{entry.fileName}</h2>
        {summary && (
          <p className={cn("mt-1 text-xs font-bold uppercase tracking-wide", summary.tone === "issue" ? "text-[#F17893]" : "text-[#9CA3AF]")}>
            {summary.text}
          </p>
        )}
        {/* Care & Treatment: what this upload added (visits, notes, calls). */}
        {entry.status === "complete" && entry.uploadId && (
          <UploadCareSummary uploadId={entry.uploadId} actingAsProfileId={actingAsProfileId} />
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <PillTabs<TabId>
            label="Upload detail view"
            active={activeTab}
            onChange={selectDataTab}
            tabs={[
              { id: "issues", label: "Issues" },
              ...(showFilesTab ? [{ id: FILES_TAB_ID, label: "Files Found" }] : []),
              ...dataTabs,
              { id: ORIGINAL_FILE_TAB_ID, label: "View Original File" },
            ]}
          />

          <div className="flex items-center">
            <div
              className={cn(
                "min-w-0 overflow-hidden transition-[max-width,opacity,margin-right] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                showExtractButton ? "mr-2 max-w-[220px] opacity-100" : "mr-0 max-w-0 opacity-0",
              )}
            >
              {!showExtractButton ? null : extractIsDropdown ? (
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: effectiveDocumentRecords.map((record, index) => ({
                      key: record.id,
                      label: documentLabel(record, index, effectiveDocumentRecords.length),
                    })),
                    onClick: ({ key }) => viewDocument(key),
                  }}
                >
                  <Button className="!flex !h-9 !items-center !gap-1.5 !whitespace-nowrap !rounded-full !bg-[#F17893] !px-4 !text-xs !font-bold !text-white hover:!bg-[#E85D7C]">
                    View Extracted Text
                    <Icon icon="lucide:chevron-down" className="h-3.5 w-3.5 shrink-0" />
                  </Button>
                </Dropdown>
              ) : (
                <Button
                  onClick={openFirstExtractedDoc}
                  className="!h-9 !whitespace-nowrap !rounded-full !bg-[#F17893] !px-4 !text-xs !font-bold !text-white hover:!bg-[#E85D7C]"
                >
                  View Extracted Text
                </Button>
              )}
            </div>
            <div
              className={cn(
                "min-w-0 overflow-hidden transition-[max-width,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                showFilterButton ? "max-w-[40px] opacity-100" : "max-w-0 opacity-0",
              )}
            >
              <Button
                aria-label="Filter records"
                onClick={() => setFilterOpen(true)}
                tabIndex={showFilterButton ? 0 : -1}
                className={cn(
                  "!flex !h-9 !w-9 !items-center !justify-center !rounded-full !bg-[#F17893] !p-0 !text-white hover:!bg-[#E85D7C] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  showFilterButton ? "!scale-100" : "!scale-75",
                )}
              >
                <Icon icon="lucide:filter" className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 min-h-0 flex-1 overflow-y-auto pr-1">
        {viewingDocumentId === EXTRACTION_PENDING_ID ? (
          <EmptyState
            icon={<Icon icon="lucide:loader-2" className="h-8 w-8 animate-spin text-[#F17893]" />}
            title="Extracted text isn't ready yet"
            description="We're still processing this upload. The extracted text will show up here once it's done."
          />
        ) : viewingDocumentId ? (
          <ExtractedTextPanel documentReferenceId={viewingDocumentId} actingAsProfileId={actingAsProfileId} />
        ) : activeTab === ORIGINAL_FILE_TAB_ID ? (
          effectiveDocumentRecords.length === 0 ? (
            isPdfContentConfirmedAbsent ? (
              <EmptyState
                icon={<Icon icon="lucide:file-x" className="h-8 w-8 text-[#9CA3AF]" />}
                title="No original file to show"
                description="This upload didn't include a PDF or image document."
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-[#8A8A8A]">
                <Spinner />
                {entry.status === "processing" ? "Still processing…" : "Loading…"}
              </div>
            )
          ) : (
            <div className="space-y-4">
              {effectiveDocumentRecords.length > 1 && (
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: effectiveDocumentRecords.map((record, index) => ({
                      key: record.id,
                      label: documentLabel(record, index, effectiveDocumentRecords.length),
                    })),
                    onClick: ({ key }) => setSelectedOriginalDocId(key),
                  }}
                >
                  <Button
                    variant="outline"
                    className="!flex !h-9 !items-center !gap-1.5 !rounded-full !border-[#E5E7EB] !bg-white !px-4 !text-xs !font-bold !text-[#0A2947]"
                  >
                    {selectedOriginalDoc
                      ? documentLabel(selectedOriginalDoc, effectiveDocumentRecords.indexOf(selectedOriginalDoc), effectiveDocumentRecords.length)
                      : "Choose a file"}
                    <Icon icon="lucide:chevron-down" className="h-3.5 w-3.5 shrink-0" />
                  </Button>
                </Dropdown>
              )}
              {selectedOriginalDoc && (
                <OriginalFileTab documentReferenceId={selectedOriginalDoc.id} actingAsProfileId={actingAsProfileId} />
              )}
            </div>
          )
        ) : activeTab === FILES_TAB_ID ? (
          <FilesFoundList files={files} />
        ) : activeTab === "issues" ? (
          <UploadIssueSections
            uploadId={entry.uploadId}
            status={entry.status}
            issues={issues}
            isLoadingContents={isLoadingContents}
            actingAsProfileId={actingAsProfileId}
          />
        ) : error ? (
          <div className="flex items-center justify-between gap-4 rounded-xl bg-[#FFF5F7] px-4 py-3">
            <p className="text-sm text-[#F36F8F]">{error}</p>
            <Button variant="outline" onClick={retryLoad} className="!h-8 !shrink-0 !rounded-full !border-[#E5E7EB] !bg-white !px-3 !text-xs !font-bold">
              Try Again
            </Button>
          </div>
        ) : records === null ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-[#8A8A8A]">
            <Spinner />
            Loading…
          </div>
        ) : visibleGrouped.length === 0 && filterTypes === null && entry.status === "processing" ? (
          // Zero records so far while still processing isn't the same claim as
          // "this upload has none" (the empty-state copy below) — the job may
          // simply not have reached a page with this data yet.
          <EmptyState
            icon={<Icon icon="lucide:loader-2" className="h-8 w-8 animate-spin text-[#F17893]" />}
            title="Still processing"
            description="We're still extracting this upload. Records will appear here as soon as they're found."
          />
        ) : visibleGrouped.length === 0 && filterTypes === null && activeTab === "pdf" && documentRecords.length > 0 ? (
          // A PDF/scanned document genuinely exists and was processed here
          // (documentRecords is non-empty, and "View Extracted Text" is
          // showing) — this is NOT the same claim as "no PDF was found in
          // this upload" below. Zero clinical cards just means nothing
          // structured was extracted from it (a blank/non-clinical page, or
          // a permanently-failed conversion), which reads very differently
          // to a patient than "you didn't upload a PDF."
          <EmptyState
            icon={<Icon icon="lucide:file-search" className="h-8 w-8 text-[#9CA3AF]" />}
            title="No clinical data extracted"
            description="We found and processed this PDF, but didn't find any structured clinical information in it."
          />
        ) : visibleGrouped.length === 0 ? (
          <EmptyState
            icon={<Icon icon="lucide:file-search" className="h-8 w-8 text-[#9CA3AF]" />}
            title={filterTypes !== null ? "No records match" : activeTab === "pdf" ? "No PDF data found" : "No records found"}
            description={
              filterTypes !== null
                ? "No records match the current filter."
                : activeTab === "pdf"
                  ? "This upload didn't include any scanned or PDF documents."
                  : "This upload didn't include any parsed structured records."
            }
          />
        ) : (
          <div className="space-y-5">
            {visibleGrouped.map(({ type, meta, items, total }) => {
              const displayItems = type === "Practitioner"
                ? collapsePractitionerDuplicates(items)
                : items.map((record) => ({ record, duplicateCount: 1 }));
              return (
                <div key={type}>
                  <div className="mb-2 flex items-center gap-2">
                    <Icon icon={meta.icon} className="h-4 w-4 text-[#9CA3AF]" />
                    <h3 className="text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">
                      {meta.label} ({total})
                    </h3>
                  </div>
                  {items.length < total && (
                    <p className="mb-2 text-xs font-medium text-[#9CA3AF]">
                      Showing the newest {items.length} of {total}.
                    </p>
                  )}
                  <div className="space-y-3">
                    {displayItems.map(({ record, duplicateCount }) => {
                      const fields = visibleFields(record.fields);
                      return (
                      <div key={record.id} className="rounded-xl bg-[#F3F4F6] px-4 py-3">
                        {duplicateCount > 1 && (
                          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">
                            Appears in {duplicateCount} source records
                          </p>
                        )}
                        {fields.length === 0 ? (
                          <p className="text-sm font-medium text-[#9CA3AF]">No additional details available</p>
                        ) : (
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 md:grid-cols-3">
                          {fields.map(([key, value]) => (
                            <div key={key} className="min-w-0">
                              <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">{formatLabel(key)}</p>
                              <p className="truncate text-sm font-semibold text-[#111827]" title={formatValue(value)}>
                                {formatValue(value)}
                              </p>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  {/* A red text link on the right; mt-5 matches the space-y-5 below, so it sits
                      midway between the cards above and the next section. */}
                  {items.length < total && (
                    <div className="mt-5 flex justify-end">
                      <button
                        type="button"
                        onClick={() => loadMore(type, items.length)}
                        disabled={loadingMoreType === type}
                        className="text-sm font-bold text-[#E4002B]/80 transition-colors hover:text-[#E4002B] disabled:opacity-50"
                      >
                        {loadingMoreType === type ? "Loading…" : `Show more (${total - items.length} more)`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {filterOpen && (
        <RecordsFilterModal
          options={grouped.map((g) => ({ type: g.type, label: g.meta.label }))}
          selected={filterTypes}
          onApply={(types) => {
            // Selecting literally everything is equivalent to no filter at
            // all (same visible result either way), so that case simplifies
            // back to null. An EMPTY selection ("Clear all" + Apply) must
            // NOT be treated the same way — that used to fall back to
            // showing everything, defeating the entire point of clearing
            // every checkbox and applying. types=[] is kept as a real,
            // explicit filter here so visibleGrouped's own `.includes(...)`
            // check (below) correctly shows nothing, matching what the
            // patient actually asked for.
            setFilterTypes(types.length === grouped.length ? null : types);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </div>
  );
}
